import {useCallback, useEffect, useRef, useState} from 'react'
import {documentApi} from '../services/documentService'

interface UseDocumentDescriptionProps {
    documentId: string
    description: string
}

interface UseDocumentDescriptionReturn {
    editMode: boolean
    content: string
    setContent: React.Dispatch<React.SetStateAction<string>>
    saveStatus: 'not_changed' | 'changed' | 'saved'
    lastSavedTime: Date | null
    toggleEditMode: () => void
    enterEditMode: () => void
    cancelEdit: () => void
    handleManualSave: () => void
}

export const useDocumentDescription = ({
                                           documentId,
                                           description,
                                       }: UseDocumentDescriptionProps): UseDocumentDescriptionReturn => {
    const [editMode, setEditMode] = useState(false)
    const [content, setContent] = useState(description)
    const [originalContent, setOriginalContent] = useState(description)
    const [saveStatus, setSaveStatus] = useState<'not_changed' | 'changed' | 'saved'>('not_changed')
    const [lastSavedTime, setLastSavedTime] = useState<Date | null>(null)
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    // Keep the draft in sync with remote changes while not editing.
    useEffect(() => {
        if (!editMode) {
            setContent(description)
            setOriginalContent(description)
            setSaveStatus('not_changed')
        }
    }, [description, editMode])

    const saveDescription = useCallback((manualSave: boolean = false) => {
        documentApi.changeDescription(documentId, content)
        setSaveStatus('saved')
        setLastSavedTime(new Date())
        if (manualSave) {
            setEditMode(false)
        } else {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current)
                timeoutRef.current = null
            }
        }
    }, [documentId, content])

    const toggleEditMode = useCallback(() => {
        setEditMode(!editMode)
        if (!editMode) {
            setContent(description)
            setOriginalContent(description)
            setSaveStatus('not_changed')
        } else {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current)
                timeoutRef.current = null
            }
            if (content !== description) {
                saveDescription(true)
            }
        }
    }, [editMode, description, content, saveDescription])

    const enterEditMode = useCallback(() => {
        if (!editMode) {
            setEditMode(true)
            setContent(description)
            setOriginalContent(description)
            setSaveStatus('not_changed')
        }
    }, [editMode, description])

    const cancelEdit = useCallback(() => {
        setEditMode(false)
        setContent(originalContent)
        setSaveStatus('not_changed')
    }, [originalContent])

    const handleManualSave = useCallback(() => {
        saveDescription(true)
    }, [saveDescription])

    useEffect(() => {
        if (editMode && content !== originalContent) {
            setSaveStatus('changed')
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current)
            }
            timeoutRef.current = setTimeout(() => {
                saveDescription()
            }, 2000)
        } else if (editMode && content === originalContent) {
            setSaveStatus('not_changed')
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current)
                timeoutRef.current = null
            }
        }
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current)
                timeoutRef.current = null
            }
        }
    }, [editMode, content, originalContent, saveDescription])

    return {
        editMode,
        content,
        setContent,
        saveStatus,
        lastSavedTime,
        toggleEditMode,
        enterEditMode,
        cancelEdit,
        handleManualSave,
    }
}
